import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { RegistrationApiService } from '../../registration/api/RegistrationApiService';

interface ProfileProgressBarProps {
  className?: string;
  /** Onboarding an existing clinic has no pledges and no payment: show only its own steps. */
  onboarding?: boolean;
}

const ProfileProgressBar: React.FC<ProfileProgressBarProps> = ({
  className = '',
  onboarding = false
}) => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Map current route to step
  const routeToStep: Record<string, string> = {
    '/clinic_info_registration': 'clinic_info',
    '/document_upload': 'uploading_docs',
    '/agree_terms': 'agree_terms',
    '/payment': 'pay'
  };

  const currentProgress = routeToStep[location.pathname] || 'clinic_info';

  // Define clinic registration steps in order (post minimum registration)
  const profileSteps = [
    'clinic_info',
    'uploading_docs',
    'agree_terms',
    'pay'
  ];

  // Find current step index
  const currentStepIndex = profileSteps.indexOf(currentProgress || '');

  const steps = [
    {
      id: 'clinic_info',
      route: '/clinic_info_registration',
      label: t('clinic_info_step'),
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ),
      stepIndex: 0,
      isCompleted: currentStepIndex > 0,
      isCurrent: currentProgress === 'clinic_info'
    },
    {
      id: 'uploading_docs',
      route: '/document_upload',
      label: t('documents_step'),
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
        </svg>
      ),
      stepIndex: 1,
      isCompleted: currentStepIndex > 1,
      isCurrent: currentProgress === 'uploading_docs'
    },
    {
      id: 'agree_terms',
      route: '/agree_terms',
      label: t('terms_step'),
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
        </svg>
      ),
      stepIndex: 2,
      isCompleted: currentStepIndex > 2,
      isCurrent: currentProgress === 'agree_terms'
    },
    {
      id: 'pay',
      route: '/payment',
      label: t('payment'),
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20,8H4V6H20M20,18H4V12H20M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"/>
        </svg>
      ),
      stepIndex: 3,
      isCompleted: currentStepIndex > 3,
      isCurrent: currentProgress === 'pay'
    }
  ];

  // Add isClickable property to each step
  const stepsWithClickable = steps.filter(step => !onboarding || (step.id !== 'agree_terms' && step.id !== 'pay')).map(step => ({
    ...step,
    // Allow navigation to current step and all previous steps
    isClickable: step.stepIndex <= currentStepIndex
  }));

  const handleStepClick = async (step: any) => {
    if (!step.isClickable) return;
    
    try {
      // For backward navigation (going to previous steps), allow direct navigation
      if (step.stepIndex < currentStepIndex) {
        console.log('🔙 Navigating backward to:', step.id, 'from step:', currentProgress);
        navigate(step.route, { replace: true, state: { backwardNavigation: true, timestamp: Date.now() } });
        return;
      }
      
      // For current step navigation, allow direct navigation
      if (step.stepIndex === currentStepIndex) {
        console.log('🔄 Navigating to current step:', step.id);
        navigate(step.route, { replace: true, state: { currentStepNavigation: true, timestamp: Date.now() } });
        return;
      }
      
      // For forward navigation, use backend validation (original behavior)
      const registrationApi = new RegistrationApiService();
      const validation = await registrationApi.validateNavigation(step.id);
      
      if (validation.isSuccess && validation.data?.can_navigate) {
        console.log('✅ Forward navigation validated for:', step.id);
        navigate(step.route, { replace: true, state: { validatedNavigation: true, timestamp: Date.now() } });
      } else {
        console.warn('❌ Forward navigation denied for:', step.id, validation.error);
        // Don't navigate forward if validation fails
      }
    } catch (error) {
      console.error('Navigation validation failed:', error);
      // Fallback to direct navigation for current and backward steps only
      if (step.stepIndex <= currentStepIndex) {
        navigate(step.route, { replace: true });
      }
    }
  };

  return (
    <div className={`bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3 shadow-sm ${className} mb-4`}>
      {/* Section Title */}
      <div className={`text-center mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
        <h4 className="text-sm font-semibold text-indigo-700">
          {t('clinic_registration')}
        </h4>
      </div>

      {/* Steps - Single row with 4 steps */}
      <div className="flex items-center justify-between">
        {stepsWithClickable.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center relative z-10">
                <button
                  onClick={() => handleStepClick(step)}
                  disabled={!step.isClickable}
                  className={`group relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 transform ${
                    step.isCompleted
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md hover:shadow-lg hover:scale-105'
                      : step.isCurrent
                      ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md animate-pulse'
                      : step.isClickable
                      ? 'bg-white text-gray-600 border-2 border-gray-300 hover:border-indigo-400 hover:text-indigo-600 hover:shadow-sm hover:scale-105 shadow-sm'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200'
                  } ${step.isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                >
                  {step.isCompleted ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="scale-75">{step.icon}</div>
                  )}
                  
                  {/* Hover tooltip */}
                  {step.isClickable && (
                    <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      {step.label}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
                    </div>
                  )}
                </button>
                
                {/* Step Label */}
                <span className={`mt-1 text-xs font-medium text-center px-1 transition-colors duration-300 leading-tight ${
                  step.isCurrent 
                    ? 'text-indigo-700' 
                    : step.isCompleted
                    ? 'text-green-700'
                    : step.isClickable
                    ? 'text-gray-700'
                    : 'text-gray-400'
                }`}>
                  {step.label}
                </span>
                
                {/* Current step indicator */}
                {step.isCurrent && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
                  </div>
                )}
              </div>

              {/* Connector Line */}
              {index < stepsWithClickable.length - 1 && (
                <div className="flex-1 mx-4 relative">
                  <div className="h-0.5 bg-gray-200 rounded-full"></div>
                  <div className={`absolute top-0 h-0.5 rounded-full transition-all duration-500 ease-out ${
                    step.isCompleted
                      ? 'w-full bg-gradient-to-r from-green-400 to-green-500'
                      : 'w-0 bg-gradient-to-r from-indigo-400 to-indigo-500'
                  }`}>
                    {step.isCompleted && (
                      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

export default ProfileProgressBar;

export {}; 